import {
	WebGLRenderTarget,
	LinearFilter,
	RGBAFormat,
	PlaneGeometry,
	Mesh,
	OrthographicCamera,
} from "three";
import { GaussianBlurMaterial } from "postprocessing";

class BlurSystem {
	backgroundRenderTarget = null;
	intermediateBlurRenderTarget = null; // Render target for horizontal blur pass
	finalBlurRenderTarget = null; // Render target for final blurred result
	blurAmount = 0; // 0 = no blur, 1 = full blur
	horizontalBlurMaterial = null; // GaussianBlurMaterial for horizontal pass
	verticalBlurMaterial = null; // GaussianBlurMaterial for vertical pass
	blurQuad = null; // Fullscreen quad for blur passes

	setup(canvasRect, dpr) {
		const width = canvasRect.width * dpr;
		const height = canvasRect.height * dpr;

		// Create render targets for blur passes
		this.backgroundRenderTarget = new WebGLRenderTarget(
			Math.floor(width),
			Math.floor(height),
			{
				minFilter: LinearFilter,
				magFilter: LinearFilter,
				format: RGBAFormat,
			}
		);

		// Intermediate render target for horizontal blur pass
		this.intermediateBlurRenderTarget = new WebGLRenderTarget(
			Math.floor(width),
			Math.floor(height),
			{
				minFilter: LinearFilter,
				magFilter: LinearFilter,
				format: RGBAFormat,
			}
		);

		// Final render target for vertical blur pass
		this.finalBlurRenderTarget = new WebGLRenderTarget(
			Math.floor(width),
			Math.floor(height),
			{
				minFilter: LinearFilter,
				magFilter: LinearFilter,
				format: RGBAFormat,
			}
		);

		// Create two GaussianBlurMaterials for two-pass blur
		// Horizontal blur material (direction: 1, 0)
		this.horizontalBlurMaterial = new GaussianBlurMaterial({
			kernelSize: 3,
		});
		this.horizontalBlurMaterial.setSize(width, height);
		if (this.horizontalBlurMaterial.uniforms && this.horizontalBlurMaterial.uniforms.direction) {
			this.horizontalBlurMaterial.uniforms.direction.value.set(1, 0);
		}

		// Vertical blur material (direction: 0, 1)
		this.verticalBlurMaterial = new GaussianBlurMaterial({
			kernelSize: 3,
		});
		this.verticalBlurMaterial.setSize(width, height);
		if (this.verticalBlurMaterial.uniforms && this.verticalBlurMaterial.uniforms.direction) {
			this.verticalBlurMaterial.uniforms.direction.value.set(0, 1);
		}

		// Create a fullscreen quad for blur passes (reused for both passes)
		const quadGeometry = new PlaneGeometry(2, 2);
		this.blurQuad = new Mesh(quadGeometry, this.horizontalBlurMaterial);
	}

	setBlurAmount(amount) {
		// Clamp between 0 and 1
		this.blurAmount = Math.max(0, Math.min(1, amount));

		// Update both blur materials' kernel size based on blur amount
		// Map 0-1 to kernel size range (3-1020, must be odd)
		// Use a reasonable range like 3-35 for performance
		const minKernel = 3;
		const maxKernel = 299;
		const kernelSize = Math.floor(minKernel + (maxKernel - minKernel) * this.blurAmount);
		const oddKernelSize = kernelSize % 2 === 0 ? kernelSize + 1 : kernelSize;

		if (this.horizontalBlurMaterial) {
			this.horizontalBlurMaterial.kernelSize = oddKernelSize;
		}
		if (this.verticalBlurMaterial) {
			this.verticalBlurMaterial.kernelSize = oddKernelSize;
		}
	}

	updateSize(canvasRect, dpr) {
		if (!this.backgroundRenderTarget) {
			return;
		}

		const width = canvasRect.width * dpr;
		const height = canvasRect.height * dpr;

		this.backgroundRenderTarget.setSize(Math.floor(width), Math.floor(height));

		// Update blur materials and render targets size
		if (this.horizontalBlurMaterial) {
			this.horizontalBlurMaterial.setSize(width, height);
		}
		if (this.verticalBlurMaterial) {
			this.verticalBlurMaterial.setSize(width, height);
		}
		if (this.intermediateBlurRenderTarget) {
			this.intermediateBlurRenderTarget.setSize(Math.floor(width), Math.floor(height));
		}
		if (this.finalBlurRenderTarget) {
			this.finalBlurRenderTarget.setSize(Math.floor(width), Math.floor(height));
		}
	}

	applyBlur(renderer) {
		// Two-pass Gaussian blur: horizontal then vertical
		// Pass 1: Horizontal blur (backgroundRenderTarget -> intermediateBlurRenderTarget)
		this.horizontalBlurMaterial.inputBuffer = this.backgroundRenderTarget.texture;
		this.blurQuad.material = this.horizontalBlurMaterial;
		renderer.setRenderTarget(this.intermediateBlurRenderTarget);
		renderer.clear();
		renderer.render(this.blurQuad, new OrthographicCamera(-1, 1, 1, -1, 0, 1));

		// Pass 2: Vertical blur (intermediateBlurRenderTarget -> finalBlurRenderTarget)
		this.verticalBlurMaterial.inputBuffer = this.intermediateBlurRenderTarget.texture;
		this.blurQuad.material = this.verticalBlurMaterial;
		renderer.setRenderTarget(this.finalBlurRenderTarget);
		renderer.clear();
		renderer.render(this.blurQuad, new OrthographicCamera(-1, 1, 1, -1, 0, 1));
	}

	get finalBlurTexture() {
		return this.finalBlurRenderTarget ? this.finalBlurRenderTarget.texture : null;
	}
}

export default BlurSystem;

